import entries from 'lodash-es/entries'
import get from 'lodash-es/get'
import isArray from 'lodash-es/isArray'
import last from 'lodash-es/last'
import lastIndexOf from 'lodash-es/lastIndexOf'
import map from 'lodash-es/map'
import omit from 'lodash-es/omit'
import * as Yup from 'yup'

import {
  EMPTY_LIST,
  EMPTY_OBJECT,
  EMPTY_STRING,
} from '../../../constants/common'
import { REG_EXP_DECIMAL, REG_EXP_EMAIL } from '../../../constants/regExp'
import { LOCAL_STORAGE_WIZARD_UUID_KEY } from '../../../constants/storage'
import i18n from '../../../i18n'
import { isValueObjectType } from '../../../utils/common'
import { parseAmount } from '../../../utils/number'
import {
  getStorageItem,
  removeStorageItem,
  setStorageItem,
} from '../../../utils/storage'
import { isDwellerRole, isStaffUser } from '../../../utils/user'
import { getPhoneSelectFormikFieldValidator } from '../../Phone'

import {
  CARD_STAGE,
  INIT_STAGE,
  REQUEST_WIZARD_CARD_IMAGE_TYPE,
  REQUEST_WIZARD_CARD_LINK_TYPE,
  REQUEST_WIZARD_CARD_TEXT_TYPE,
  REQUEST_WIZARD_CARD_VARIABLE_TYPE,
  REQUEST_WIZARD_CHUNK_WORKFLOW_RESULT_TYPE,
  REQUEST_WIZARD_CREATION_FORM_SERVER_ERROR_UNABLE_TO_PERFORM_ACTION,
  REQUEST_WIZARD_CREATION_FORM_SERVER_ERRORS,
  REQUEST_WIZARD_FIELD_DECIMAL_VARIABLE_TYPE,
  REQUEST_WIZARD_FIELD_EMAIL_VARIABLE_TYPE,
  REQUEST_WIZARD_FIELD_FILE_VARIABLE_TYPE,
  REQUEST_WIZARD_FIELD_PHONE_VARIABLE_TYPE,
  REQUEST_WIZARD_FIELD_STRING_VARIABLE_TYPE,
  REQUEST_WIZARD_FIELD_TEXT_VARIABLE_TYPE,
  REQUEST_WIZARD_FORM_INIT_STAGE_INITIAL_VALUES,
  REQUEST_WIZARD_INIT_PAYLOAD_FIELD_NAME_ORIGINAL_REQUEST,
  REQUEST_WIZARD_INIT_STAGE_FIELD_NAME_BUILDING,
  REQUEST_WIZARD_INIT_STAGE_FIELD_NAME_CONTRACT,
  REQUEST_WIZARD_INIT_STAGE_FIELD_NAME_FLAT,
  REQUEST_WIZARD_INIT_STAGE_FIELD_NAME_MANAGER,
  REQUEST_WIZARD_INIT_STAGE_FIELD_NAME_USER,
  REQUEST_WIZARD_INIT_STAGE_VARIABLE_NAME_BUILDING,
  REQUEST_WIZARD_INIT_STAGE_VARIABLE_NAME_CHAT_MESSAGE,
  REQUEST_WIZARD_INIT_STAGE_VARIABLE_NAME_EMAIL_MESSAGE,
  REQUEST_WIZARD_INIT_STAGE_VARIABLE_NAME_FLAT,
  REQUEST_WIZARD_INIT_STAGE_VARIABLE_NAME_IS_CHATROOM_PUBLIC,
  REQUEST_WIZARD_INIT_STAGE_VARIABLE_NAME_MANAGER,
  REQUEST_WIZARD_INIT_STAGE_VARIABLE_NAME_POST,
  REQUEST_WIZARD_INIT_STAGE_VARIABLE_NAME_PROFILE,
  RESTORE_STAGE,
} from './RequestWizardForm.constants'

// The draft uuid is intentionally NOT compared against the live session
// uuid. The uuid is a server-session token; the wizard server recycles
// it every time the modal closes (its prolong interval is torn down, so
// the next GET /chunk/ returns 403, forcing initWizard to mint a fresh
// uuid). The semantic "same workflow step" identity is
// `stage + chunkSignature`, which proves the saved values target the
// same variables in the same order. The uuid is still kept in the draft
// envelope so handleOpenDraft can re-stamp LOCAL_STORAGE_WIZARD_UUID_KEY
// and try the original session first — when that succeeds (session still
// alive) the user lands on CARD_STAGE directly; when it fails (403) the
// user makes one trip through INIT_STAGE and then this restore kicks in
// on the new session.
function isWizardDraftCompatible(draft, { chunk, stage }) {
  const matchesStage = draft.stage === stage
  const matchesChunkSignature =
    (draft.chunkSignature ?? null) === (getWizardChunkSignature(chunk) ?? null)

  return matchesStage && matchesChunkSignature
}

// Common utils

export function isRequestWizardInitStage(stage) {
  return stage === INIT_STAGE
}

export function isRequestWizardCardStage(stage) {
  return stage === CARD_STAGE
}

export function isRequestWizardRestoreStage(stage) {
  return stage === RESTORE_STAGE
}

export function saveWizardUuid(uuid) {
  setStorageItem(LOCAL_STORAGE_WIZARD_UUID_KEY, uuid)
}

export function clearWizardUuid() {
  removeStorageItem(LOCAL_STORAGE_WIZARD_UUID_KEY)
}

export function getWizardPrevUuid() {
  return getStorageItem(LOCAL_STORAGE_WIZARD_UUID_KEY)
}

export function isFormFilled(values) {
  const formValues = omit(values, [
    REQUEST_WIZARD_INIT_STAGE_FIELD_NAME_CONTRACT,
    REQUEST_WIZARD_INIT_STAGE_FIELD_NAME_MANAGER,
  ])

  return Object.values(formValues).every(field => !!field)
}

export function isFilledWithDweller(userField) {
  return Number.isFinite(userField?.value) && isDwellerRole(userField)
}

export function getWizardChunkSignature(chunk) {
  return chunk?.length > 0
    ? chunk
        .map(
          card => `${card.name ?? EMPTY_STRING}:${card.type ?? EMPTY_STRING}`
        )
        .join('|')
    : null
}

export function applyRequestWizardDraftSafely(draft, params = EMPTY_OBJECT) {
  return draft &&
    isValueObjectType(draft) &&
    isWizardDraftCompatible(draft, params)
    ? (draft.values ?? null)
    : null
}

export function isWizardDraftStaleForStage({
  chunk,
  draft,
  draftValuesForStage,
  stage,
}) {
  return (
    !!draft &&
    chunk?.length > 0 &&
    draftValuesForStage == null &&
    draft.stage === stage
  )
}

export function isRequestWizardCardVariableType(type) {
  return type === REQUEST_WIZARD_CARD_VARIABLE_TYPE
}

export function isRequestWizardChunkWorkflowResultType(type) {
  return type === REQUEST_WIZARD_CHUNK_WORKFLOW_RESULT_TYPE
}

export function getRequestWizardChunkCards(chunk) {
  return chunk.filter(
    element => !isRequestWizardChunkWorkflowResultType(element.type)
  )
}

export function getRequestWizardChunkCreatedRequests(chunk) {
  return (
    chunk.find(element => isRequestWizardChunkWorkflowResultType(element.type))
      ?.requests ?? EMPTY_LIST
  )
}

export function parseRequestWizardCreationFormServerError(data, t) {
  const errorText = data?.errors ?? EMPTY_STRING

  if (REQUEST_WIZARD_CREATION_FORM_SERVER_ERRORS[errorText]) {
    switch (REQUEST_WIZARD_CREATION_FORM_SERVER_ERRORS[errorText]) {
      case REQUEST_WIZARD_CREATION_FORM_SERVER_ERRORS[
        REQUEST_WIZARD_CREATION_FORM_SERVER_ERROR_UNABLE_TO_PERFORM_ACTION
      ]:
        return t(
          REQUEST_WIZARD_CREATION_FORM_SERVER_ERRORS[
            REQUEST_WIZARD_CREATION_FORM_SERVER_ERROR_UNABLE_TO_PERFORM_ACTION
          ]
        )

      default:
        return t(REQUEST_WIZARD_CREATION_FORM_SERVER_ERRORS[errorText])
    }
  }

  return errorText
}

// Init stage utils

export function getInitStageInitialValues({
  initialBuilding,
  initialFlat,
} = EMPTY_OBJECT) {
  return {
    ...REQUEST_WIZARD_FORM_INIT_STAGE_INITIAL_VALUES,
    ...(initialBuilding
      ? { [REQUEST_WIZARD_INIT_STAGE_FIELD_NAME_BUILDING]: initialBuilding }
      : null),
    ...(initialFlat
      ? { [REQUEST_WIZARD_INIT_STAGE_FIELD_NAME_FLAT]: initialFlat }
      : null),
  }
}

export function getInitStageValidationSchema() {
  return Yup.object({
    building: Yup.object().when(REQUEST_WIZARD_INIT_STAGE_FIELD_NAME_USER, {
      is: user => Number.isFinite(user?.value) && isDwellerRole(user),
      then: schema => schema.required(true),
    }),
    flat: Yup.object().when(REQUEST_WIZARD_INIT_STAGE_FIELD_NAME_USER, {
      is: user => Number.isFinite(user?.value) && isDwellerRole(user),
      then: schema => schema.required(true),
    }),
    user: Yup.object(),
  })
}

export function getInitStagePayload({ originalRequestId }) {
  return originalRequestId
    ? {
        [REQUEST_WIZARD_INIT_PAYLOAD_FIELD_NAME_ORIGINAL_REQUEST]:
          originalRequestId,
      }
    : undefined
}

export function getInitStageVariables({
  building,
  chatMessageUuid,
  currentUser,
  flat,
  manager,
  messageUuid,
  postUuid,
  submittedUser,
}) {
  const profile = submittedUser || currentUser

  const variables = [
    {
      name: REQUEST_WIZARD_INIT_STAGE_VARIABLE_NAME_PROFILE,
      value: profile.value || profile.id || profile.owner,
    },
  ]

  if (flat) {
    variables.push({
      name: REQUEST_WIZARD_INIT_STAGE_VARIABLE_NAME_FLAT,
      value: flat.value,
    })
  } else if (building) {
    variables.push({
      name: REQUEST_WIZARD_INIT_STAGE_VARIABLE_NAME_BUILDING,
      value: building.value,
    })
  }

  if (messageUuid) {
    variables.push({
      name: REQUEST_WIZARD_INIT_STAGE_VARIABLE_NAME_EMAIL_MESSAGE,
      value: messageUuid,
    })
  }

  if (chatMessageUuid) {
    variables.push({
      name: REQUEST_WIZARD_INIT_STAGE_VARIABLE_NAME_CHAT_MESSAGE,
      value: chatMessageUuid,
    })
  }

  if (postUuid) {
    variables.push({
      name: REQUEST_WIZARD_INIT_STAGE_VARIABLE_NAME_POST,
      value: postUuid,
    })
  }

  if (manager) {
    variables.push({
      name: REQUEST_WIZARD_INIT_STAGE_VARIABLE_NAME_MANAGER,
      value: manager.value,
    })
  }

  return variables
}

export function getInitStageSubmitHandler({
  chatMessageUuid,
  currentUser,
  isChatroomPublic,
  isSingleFlat,
  messageUuid,
  postUuid,
  onInit,
}) {
  return function (values, { setErrors }) {
    const user = values[REQUEST_WIZARD_INIT_STAGE_FIELD_NAME_USER]

    const variables = getInitStageVariables({
      building:
        !isSingleFlat &&
        values[REQUEST_WIZARD_INIT_STAGE_FIELD_NAME_BUILDING]?.value
          ? values[REQUEST_WIZARD_INIT_STAGE_FIELD_NAME_BUILDING]
          : null,
      chatMessageUuid,
      currentUser,
      flat:
        !isSingleFlat &&
        values[REQUEST_WIZARD_INIT_STAGE_FIELD_NAME_FLAT]?.value &&
        values[REQUEST_WIZARD_INIT_STAGE_FIELD_NAME_BUILDING]?.value
          ? values[REQUEST_WIZARD_INIT_STAGE_FIELD_NAME_FLAT]
          : null,
      manager:
        isStaffUser(currentUser) &&
        Number.isFinite(
          values[REQUEST_WIZARD_INIT_STAGE_FIELD_NAME_MANAGER]?.value
        )
          ? values[REQUEST_WIZARD_INIT_STAGE_FIELD_NAME_MANAGER]
          : null,
      messageUuid,
      postUuid,
      submittedUser: user,
    })

    onInit({
      data: {
        ...(isChatroomPublic !== undefined && {
          [REQUEST_WIZARD_INIT_STAGE_VARIABLE_NAME_IS_CHATROOM_PUBLIC]:
            isChatroomPublic,
        }),
        variables: JSON.stringify(variables),
      },
      setErrors,
    })
  }
}

// Card stage utils

export function isBotVisible(chunk) {
  if (!chunk) {
    return false
  }

  if (chunk.length === 0) {
    return true
  }

  const firstCardType = chunk[0].type

  return (
    isRequestWizardCardTextType(firstCardType) ||
    isRequestWizardCardImageType(firstCardType)
  )
}

export function getCardStageVariables(chunk) {
  return chunk.filter(card => isRequestWizardCardVariableType(card.type))
}

export function getCardStageLinks(chunk) {
  return chunk.filter(card => isRequestWizardCardLinkType(card.type))
}

export function getCardStageLinkOptions(chunk) {
  if (!chunk || chunk.length === 0) {
    return EMPTY_LIST
  }

  // Identify link options by `id` (unique), not `block`, which can repeat
  // across links. The AI Wizard matches Greta's suggestion against `value`
  // (now an `id`); `block` is kept on the option for the submit payload.
  return getCardStageLinks(chunk).flatMap(card =>
    (card.links ?? EMPTY_LIST).map(link => ({
      block: link.block,
      label: link.name,
      value: link.id,
    }))
  )
}

export function getCardStageInitialValues(chunk) {
  if (!chunk || chunk.length === 0) {
    return null
  }

  const variables = getCardStageVariables(chunk)
  const links = getCardStageLinks(chunk)

  const variablesValues = variables.reduce((acc, entry) => {
    if (isRequestWizardFieldFileType(entry.variable_type)) {
      acc[entry.name] = EMPTY_LIST
    } else {
      acc[entry.name] = EMPTY_STRING
    }

    return acc
  }, {})

  if (links && links.length > 0) {
    return { ...variablesValues, link: EMPTY_STRING }
  }

  return variablesValues
}

export function getCardStageValidationSchema(chunk) {
  if (!chunk || chunk.length === 0) {
    return null
  }

  const variables = getCardStageVariables(chunk)
  const links = getCardStageLinks(chunk)

  const variablesSchema = variables.reduce((acc, entry) => {
    let validator = Yup.string()

    if (isRequestWizardFieldDecimalType(entry.variable_type)) {
      validator = Yup.string().matches(
        REG_EXP_DECIMAL,
        i18n.t('Error.WizardIntegerInputIncorrectValueTypeKey')
      )
    } else if (isRequestWizardFieldEmailType(entry.variable_type)) {
      validator = Yup.string().matches(
        REG_EXP_EMAIL,
        i18n.t('Error.WizardEmailInputIncorrectValueKey')
      )
    } else if (isRequestWizardFieldFileType(entry.variable_type)) {
      validator = Yup.array().of(Yup.object())
    } else if (isRequestWizardFieldPhoneType(entry.variable_type)) {
      validator = getPhoneSelectFormikFieldValidator()
    }

    if (entry.required) {
      if (validator.type === 'array') {
        validator = validator.min(1, () => true)
      } else {
        validator = validator.required(() => true)
      }
    }

    acc[entry.name] = validator

    return acc
  }, {})

  if (links && links.length > 0) {
    return Yup.object({
      ...variablesSchema,
      link: Yup.object().required(() => true),
    })
  }

  return Yup.object(variablesSchema)
}

export function isEmptyChunk(chunk) {
  return !!(chunk && chunk.length === 0)
}

export function isWorkflowEnd(chunk) {
  return isEmptyChunk(chunk) || isLastChunk(chunk)
}

export function isRequestWizardFinished({ chunk, stage }) {
  return isRequestWizardCardStage(stage) && isWorkflowEnd(chunk)
}

export function isLastChunk(chunk) {
  if (!chunk || chunk.length === 0) {
    return false
  }

  const cardType = last(chunk).type

  return (
    isRequestWizardCardTextType(cardType) ||
    isRequestWizardCardImageType(cardType)
  )
}

export function getCardStageDividerPosition(chunk) {
  if (!chunk || chunk.length < 2) {
    return null
  }

  const chunkTypes = chunk.map(item => item.type)

  if (!(
    (chunkTypes.includes(REQUEST_WIZARD_CARD_TEXT_TYPE) ||
      chunkTypes.includes(REQUEST_WIZARD_CARD_IMAGE_TYPE)) &&
    (chunkTypes.includes(REQUEST_WIZARD_CARD_VARIABLE_TYPE) ||
      chunkTypes.includes(REQUEST_WIZARD_CARD_LINK_TYPE))
  )) {
    return null
  }

  const lastImageIndex = lastIndexOf(chunkTypes, REQUEST_WIZARD_CARD_IMAGE_TYPE)
  const lastTextIndex = lastIndexOf(chunkTypes, REQUEST_WIZARD_CARD_TEXT_TYPE)

  return Math.max(lastImageIndex, lastTextIndex)
}

export function getCardStageSubmitHandler({
  chunk,
  isChatroomPublic,
  uuid,
  onSubmit,
}) {
  return function (values, { resetForm, setErrors }) {
    let isLink = false

    const decimalVariableNames = getCardStageVariables(chunk)
      .filter(entry => isRequestWizardFieldDecimalType(entry.variable_type))
      .map(entry => entry.name)

    const submitEntries = entries(values)

    let processedValues = submitEntries.map(entry => {
      const name = entry[0]
      const value = entry[1]

      if (isRequestWizardCardLinkType(name)) {
        isLink = true

        // Fall back to `value.value` for drafts saved before link options
        // carried `block` (their value held the block under the old shape).
        return { block: value.block ?? value.value }
      }

      return {
        name: entry[0],
        value: isArray(value) ? JSON.stringify(map(value, 'id')) : value,
      }
    })

    if (isLink) {
      processedValues = processedValues[0]
    } else {
      processedValues = {
        variables: JSON.stringify(
          processedValues
            .filter(entry => !!get(entry, 'value'))
            .map(entry =>
              decimalVariableNames.includes(entry.name)
                ? { ...entry, value: parseAmount(entry.value) }
                : entry
            )
        ),
      }
    }

    onSubmit({
      data: {
        ...processedValues,
        ...(isChatroomPublic !== undefined && {
          [REQUEST_WIZARD_INIT_STAGE_VARIABLE_NAME_IS_CHATROOM_PUBLIC]:
            isChatroomPublic,
        }),
      },
      resetForm,
      setErrors,
      uuid,
    })
  }
}

export function isRequestWizardCardTextType(type) {
  return type === REQUEST_WIZARD_CARD_TEXT_TYPE
}

export function isRequestWizardCardImageType(type) {
  return type === REQUEST_WIZARD_CARD_IMAGE_TYPE
}

export function isRequestWizardCardLinkType(type) {
  return type === REQUEST_WIZARD_CARD_LINK_TYPE
}

export function isRequestWizardFieldStringType(type) {
  return type === REQUEST_WIZARD_FIELD_STRING_VARIABLE_TYPE
}

export function isRequestWizardFieldDecimalType(type) {
  return type === REQUEST_WIZARD_FIELD_DECIMAL_VARIABLE_TYPE
}

export function isRequestWizardFieldEmailType(type) {
  return type === REQUEST_WIZARD_FIELD_EMAIL_VARIABLE_TYPE
}

export function isRequestWizardFieldPhoneType(type) {
  return type === REQUEST_WIZARD_FIELD_PHONE_VARIABLE_TYPE
}

export function isRequestWizardFieldTextType(type) {
  return type === REQUEST_WIZARD_FIELD_TEXT_VARIABLE_TYPE
}

export function isRequestWizardFieldFileType(type) {
  return type === REQUEST_WIZARD_FIELD_FILE_VARIABLE_TYPE
}

export function getCardKey(card, index) {
  return `${card.id}_${card.type}${index}`
}
